/**
 * CalendarView.jsx
 * Timeline / Gantt-style calendar.
 * Each property = one row. Bookings = coloured bars spanning their dates.
 * Much clearer than a grid when managing multiple properties.
 *
 * Features:
 * - 30-day scrollable timeline (default current month)
 * - Booking bars with guest name, colour-coded by status
 * - Click any bar for full booking details
 * - Filter by booking source
 * - Today highlighted
 * - Prev / Next month navigation
 * - Mobile horizontal scroll
 */
import React, { useState, useMemo, useRef } from "react";

// ─── Colours per status ───────────────────────────────────────────────────────
const STATUS_COLORS = {
  Upcoming:  { bar: "#d4a853", text: "#0f0f1a", border: "#b8922e" },
  Active:    { bar: "#51cf66", text: "#0f0f1a", border: "#2f9e44" },
  Completed: { bar: "#5a5870", text: "#f0eee8", border: "#3d3b50" },
  Cancelled: { bar: "#ff6b6b", text: "#fff",    border: "#c92a2a" },
};

const MONTHS = ["January","February","March","April","May","June",
                "July","August","September","October","November","December"];
const DAYS_SHORT = ["Su","Mo","Tu","We","Th","Fr","Sa"];
const SOURCE_ICONS = {
  WhatsApp:"💬", "Phone Call":"📞", Instagram:"📸", Facebook:"👥",
  Airbnb:"🏠", "Booking.com":"🌐", "Walk-in":"🚶", Other:"📝",
};

const fmt = (n) => new Intl.NumberFormat("en-PK", {
  style:"currency", currency:"PKR", maximumFractionDigits:0
}).format(n||0);

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-PK", {
  day:"numeric", month:"short", year:"numeric"
}) : "—";

// ─── Days in a given month ────────────────────────────────────────────────────
function getDays(year, month) {
  const days = [];
  const d = new Date(year, month, 1);
  while (d.getMonth() === month) { days.push(new Date(d)); d.setDate(d.getDate()+1); }
  return days;
}

// ─── Does a booking overlap with a given day? ─────────────────────────────────
function bookingOnDay(b, date) {
  const ci  = new Date(b.checkIn);
  const co  = new Date(b.checkOut);
  const day = new Date(date);
  day.setHours(0,0,0,0); ci.setHours(0,0,0,0); co.setHours(0,0,0,0);
  return day >= ci && day < co;
}

// ─── Get column span info for a booking in this month ────────────────────────
function getBarSpan(b, days) {
  const ci  = new Date(b.checkIn);  ci.setHours(0,0,0,0);
  const co  = new Date(b.checkOut); co.setHours(0,0,0,0);
  const monthStart = days[0];
  const monthEnd   = days[days.length-1];

  const startClipped = ci < monthStart ? monthStart : ci;
  const endClipped   = co > new Date(monthEnd.getTime() + 86400000) ? new Date(monthEnd.getTime() + 86400000) : co;

  const startIdx = days.findIndex(d => {
    const dd = new Date(d); dd.setHours(0,0,0,0);
    return dd.getTime() === startClipped.getTime();
  });
  if (startIdx === -1) return null;

  let span = 0;
  for (let i = startIdx; i < days.length; i++) {
    const d = new Date(days[i]); d.setHours(0,0,0,0);
    if (d >= endClipped) break;
    span++;
  }
  if (span === 0) return null;
  return { startIdx, span, isStart: ci >= monthStart, isEnd: co <= new Date(monthEnd.getTime() + 86400000) };
}

// ─── Booking detail popup ─────────────────────────────────────────────────────
function BookingDetail({ booking, color, anchorRect, onClose, onInvoice }) {
  return (
    <>
      <div onClick={onClose} style={{ position:"fixed", inset:0, zIndex:400 }} />
      <div style={{
        position:"fixed",
        top: Math.min(anchorRect.bottom + 8, window.innerHeight - 280),
        left: Math.min(anchorRect.left, window.innerWidth - 300),
        zIndex:401, width:290,
        background:"var(--bg-card)", border:"1px solid var(--border)",
        borderRadius:"var(--radius-lg)", boxShadow:"0 16px 48px rgba(0,0,0,0.5)",
        animation:"fadeIn 0.15s ease", overflow:"hidden",
      }}>
        {/* Colour strip */}
        <div style={{ background: color?.bar || "#d4a853", padding:"12px 16px" }}>
          <p style={{ fontWeight:700, fontSize:14, color: color?.text || "#0f0f1a" }}>{booking.guestName}</p>
          <p style={{ fontSize:11, color: color?.text || "#0f0f1a", opacity:0.8, marginTop:2 }}>{booking.property}</p>
        </div>
        <div style={{ padding:"14px 16px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
          {[
            { label:"Check-in",  value: fmtDate(booking.checkIn) },
            { label:"Check-out", value: fmtDate(booking.checkOut) },
            { label:"Nights",    value: `${booking.nights} nights` },
            { label:"Guests",    value: `${booking.guests} guests` },
            { label:"Total",     value: fmt(booking.totalPrice), color:"var(--accent-gold)" },
            { label:"Remaining", value: fmt(booking.remaining),
              color: booking.remaining > 0 ? "var(--accent-rose)" : "var(--accent-green)" },
          ].map(({label,value,color:c}) => (
            <div key={label}>
              <p style={{ fontSize:10, color:"var(--text-muted)", textTransform:"uppercase",
                letterSpacing:"0.06em", fontWeight:500, marginBottom:2 }}>{label}</p>
              <p style={{ fontSize:13, fontWeight:600, color: c||"var(--text-primary)" }}>{value}</p>
            </div>
          ))}
        </div>
        {booking.notes && (
          <div style={{ margin:"0 16px 12px", background:"var(--bg-secondary)",
            borderRadius:6, padding:"8px 10px", fontSize:12, color:"var(--text-secondary)" }}>
            📝 {booking.notes}
          </div>
        )}
        {booking.phone && (
          <div style={{ margin:"0 16px 12px", fontSize:12, color:"var(--text-secondary)" }}>
            📱 {booking.phone} · {SOURCE_ICONS[booking.source]||"📝"} {booking.source}
          </div>
        )}
        <div style={{ padding:"0 16px 14px", display:"flex", gap:8 }}>
          {onInvoice && (
            <button className="btn btn-primary" onClick={() => { onInvoice(booking); onClose(); }}
              style={{ flex:1, fontSize:12, padding:"7px" }}>🧾 Invoice</button>
          )}
          <button className="btn btn-ghost" onClick={onClose}
            style={{ flex:1, fontSize:12, padding:"7px" }}>Close</button>
        </div>
      </div>
    </>
  );
}

// ─── Main CalendarView ────────────────────────────────────────────────────────
export default function CalendarView({ bookings, onInvoice }) {
  const today    = new Date();
  const [year,   setYear]   = useState(today.getFullYear());
  const [month,  setMonth]  = useState(today.getMonth());
  const [filterSource, setFilterSource] = useState("All");
  const [selected, setSelected] = useState(null); // { booking, rect }
  const scrollRef = useRef();

  const prevMonth = () => { if (month===0){setMonth(11);setYear(y=>y-1);}else setMonth(m=>m-1); };
  const nextMonth = () => { if (month===11){setMonth(0);setYear(y=>y+1);}else setMonth(m=>m+1); };

  const days    = useMemo(() => getDays(year, month), [year, month]);
  const sources = useMemo(() => ["All", ...new Set(bookings.map(b=>b.source).filter(Boolean))], [bookings]);

  // All unique properties (preserve order of first appearance)
  const properties = useMemo(() => {
    const seen = new Set();
    const list = [];
    bookings.forEach(b => { if (b.property && !seen.has(b.property)) { seen.add(b.property); list.push(b.property); }});
    return list.sort();
  }, [bookings]);

  // Filter bookings
  const filtered = useMemo(() => bookings.filter(b =>
    (filterSource === "All" || b.source === filterSource) && b.status !== "Cancelled"
  ), [bookings, filterSource]);

  // Group bookings by property
  const byProperty = useMemo(() => {
    const map = {};
    properties.forEach(p => { map[p] = []; });
    filtered.forEach(b => { if (map[b.property]) map[b.property].push(b); });
    return map;
  }, [filtered, properties]);

  // Stats for this month
  const thisMonthBookings = useMemo(() => filtered.filter(b => {
    const ci = new Date(b.checkIn);
    const co = new Date(b.checkOut);
    const ms = days[0];
    const me = days[days.length-1];
    return ci <= me && co >= ms;
  }), [filtered, days]);

  const COL_W   = 38; // px per day column
  const ROW_H   = 52; // px per property row
  const LABEL_W = 180; // px for property label column

  const isToday = (d) => {
    const dd = new Date(d); dd.setHours(0,0,0,0);
    const td = new Date(today); td.setHours(0,0,0,0);
    return dd.getTime() === td.getTime();
  };
  const isWeekend = (d) => d.getDay()===0 || d.getDay()===6;

  return (
    <div>
      {/* ── Top toolbar ── */}
      <div style={{ display:"flex", gap:12, marginBottom:18, flexWrap:"wrap", alignItems:"center" }}>
        {/* Month nav */}
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <button className="btn btn-ghost" onClick={prevMonth} style={{ padding:"7px 12px", fontSize:16 }}>‹</button>
          <h3 style={{ fontSize:18, fontFamily:"var(--font-display)", minWidth:170, textAlign:"center" }}>
            {MONTHS[month]} {year}
          </h3>
          <button className="btn btn-ghost" onClick={nextMonth} style={{ padding:"7px 12px", fontSize:16 }}>›</button>
          <button className="btn btn-ghost" onClick={()=>{setMonth(today.getMonth());setYear(today.getFullYear());}}
            style={{ padding:"6px 12px", fontSize:12 }}>Today</button>
        </div>

        {/* Source filter */}
        <select value={filterSource} onChange={e=>setFilterSource(e.target.value)}
          style={{ background:"var(--bg-input)", border:"1px solid var(--border)", borderRadius:8,
            color:"var(--text-primary)", fontSize:12, padding:"8px 12px",
            fontFamily:"var(--font-body)", cursor:"pointer" }}>
          {sources.map(s => <option key={s} value={s}>{s==="All"?"All Sources":s}</option>)}
        </select>

        {/* Stats */}
        <div style={{ marginLeft:"auto", display:"flex", gap:16, flexWrap:"wrap" }}>
          {[
            { label:"Bookings this month", value: thisMonthBookings.length, color:"var(--accent-gold)" },
            { label:"Active now", value: filtered.filter(b=>b.status==="Active").length, color:"var(--accent-green)" },
            { label:"Upcoming", value: filtered.filter(b=>b.status==="Upcoming").length, color:"#4ecdc4" },
          ].map(s => (
            <div key={s.label} style={{ textAlign:"center" }}>
              <p style={{ fontSize:18, fontWeight:700, color:s.color, fontFamily:"var(--font-display)" }}>{s.value}</p>
              <p style={{ fontSize:10, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.05em" }}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Legend ── */}
      <div style={{ display:"flex", gap:16, marginBottom:14, flexWrap:"wrap" }}>
        {Object.entries(STATUS_COLORS).map(([status, c]) => (
          <div key={status} style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"var(--text-secondary)" }}>
            <div style={{ width:28, height:12, borderRadius:3, background:c.bar, border:`1px solid ${c.border}` }} />
            {status}
          </div>
        ))}
        <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"var(--text-secondary)" }}>
          <div style={{ width:12, height:12, borderRadius:2, background:"rgba(212,168,83,0.2)", border:"1px solid rgba(212,168,83,0.4)" }} />
          Today
        </div>
      </div>

      {/* ── Timeline grid ── */}
      {properties.length === 0 ? (
        <div className="card" style={{ padding:60, textAlign:"center", color:"var(--text-muted)" }}>
          <div style={{ fontSize:48, marginBottom:12 }}>🏠</div>
          <p style={{ fontSize:15 }}>No properties or bookings found</p>
          <p style={{ fontSize:13, marginTop:6 }}>Add properties and bookings to see the timeline</p>
        </div>
      ) : (
        <div className="card" style={{ overflow:"hidden" }}>
          <div style={{ overflowX:"auto" }} ref={scrollRef}>
            <div style={{ minWidth: LABEL_W + days.length * COL_W }}>

              {/* ── Date header row ── */}
              <div style={{ display:"flex", borderBottom:"1px solid var(--border)",
                background:"var(--bg-secondary)", position:"sticky", top:0, zIndex:10 }}>
                {/* Corner cell */}
                <div style={{ width:LABEL_W, minWidth:LABEL_W, flexShrink:0,
                  padding:"10px 14px", borderRight:"1px solid var(--border)",
                  fontSize:11, fontWeight:600, color:"var(--text-muted)",
                  textTransform:"uppercase", letterSpacing:"0.06em",
                  display:"flex", alignItems:"center" }}>
                  Property
                </div>
                {/* Day columns */}
                {days.map((d, i) => {
                  const td  = isToday(d);
                  const wkd = isWeekend(d);
                  return (
                    <div key={i} style={{
                      width:COL_W, minWidth:COL_W, flexShrink:0,
                      padding:"6px 0", textAlign:"center",
                      background: td ? "rgba(212,168,83,0.15)" : "transparent",
                      borderRight: i < days.length-1 ? "1px solid rgba(255,255,255,0.04)" : "none",
                    }}>
                      <p style={{ fontSize:9, color: td ? "var(--accent-gold)" : wkd ? "var(--text-secondary)" : "var(--text-muted)",
                        fontWeight: td ? 800 : 500, letterSpacing:"0.04em", textTransform:"uppercase" }}>
                        {DAYS_SHORT[d.getDay()]}
                      </p>
                      <p style={{ fontSize:13, fontWeight: td ? 800 : 500,
                        color: td ? "var(--accent-gold)" : wkd ? "var(--text-secondary)" : "var(--text-primary)",
                        marginTop:1 }}>
                        {d.getDate()}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* ── Property rows ── */}
              {properties.map((prop, rowIdx) => {
                const propBookings = byProperty[prop] || [];
                // Find all bookings that overlap this month for this property
                const visible = propBookings.filter(b => {
                  const ci = new Date(b.checkIn);
                  const co = new Date(b.checkOut);
                  return ci <= days[days.length-1] && co >= days[0];
                });

                // Build a grid: for each day, which booking is active?
                // Support multiple simultaneous bookings per property (for overlap-allowed types)
                // Stack them vertically if needed
                const lanes = []; // each lane is an array of bookings that don't overlap
                visible.forEach(b => {
                  let placed = false;
                  for (const lane of lanes) {
                    const conflicts = lane.some(lb => {
                      const lci = new Date(lb.checkIn); const lco = new Date(lb.checkOut);
                      const bci = new Date(b.checkIn);  const bco = new Date(b.checkOut);
                      return bci < lco && bco > lci;
                    });
                    if (!conflicts) { lane.push(b); placed = true; break; }
                  }
                  if (!placed) lanes.push([b]);
                });

                const rowHeight = Math.max(ROW_H, lanes.length * 28 + 12);

                return (
                  <div key={prop} style={{
                    display:"flex",
                    borderBottom: rowIdx < properties.length-1 ? "1px solid var(--border)" : "none",
                    background: rowIdx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
                    minHeight: rowHeight,
                  }}>
                    {/* Property label */}
                    <div style={{
                      width:LABEL_W, minWidth:LABEL_W, flexShrink:0,
                      padding:"10px 14px",
                      borderRight:"1px solid var(--border)",
                      display:"flex", alignItems:"flex-start", flexDirection:"column",
                      justifyContent:"center", gap:4,
                      position:"sticky", left:0, zIndex:5,
                      background: rowIdx % 2 === 0 ? "var(--bg-card)" : "rgba(22,22,42,0.98)",
                    }}>
                      <p style={{ fontSize:13, fontWeight:600, color:"var(--text-primary)",
                        whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
                        maxWidth: LABEL_W - 28 }}>
                        {prop}
                      </p>
                      <p style={{ fontSize:10, color:"var(--text-muted)" }}>
                        {propBookings.length} booking{propBookings.length!==1?"s":""} this month
                      </p>
                    </div>

                    {/* Timeline cells */}
                    <div style={{ flex:1, position:"relative", minHeight:rowHeight }}>
                      {/* Day background grid */}
                      <div style={{ display:"flex", height:"100%", position:"absolute", inset:0 }}>
                        {days.map((d,i) => (
                          <div key={i} style={{
                            width:COL_W, minWidth:COL_W, flexShrink:0, height:"100%",
                            background: isToday(d) ? "rgba(212,168,83,0.08)" : "transparent",
                            borderRight: i < days.length-1 ? "1px solid rgba(255,255,255,0.04)" : "none",
                          }} />
                        ))}
                      </div>

                      {/* Booking bars — one per lane */}
                      {lanes.map((lane, laneIdx) =>
                        lane.map(b => {
                          const span = getBarSpan(b, days);
                          if (!span) return null;
                          const c = STATUS_COLORS[b.status] || STATUS_COLORS.Upcoming;
                          const barTop  = 6 + laneIdx * 28;
                          const barLeft = span.startIdx * COL_W + 2;
                          const barW    = span.span * COL_W - 4;
                          const isTiny  = barW < 40;

                          return (
                            <div
                              key={b.bookingId}
                              onClick={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                setSelected({ booking:b, rect });
                              }}
                              title={`${b.guestName} · ${fmtDate(b.checkIn)} → ${fmtDate(b.checkOut)}`}
                              style={{
                                position:"absolute",
                                top: barTop,
                                left: barLeft,
                                width: barW,
                                height: 22,
                                background: c.bar,
                                border: `1px solid ${c.border}`,
                                borderRadius: span.isStart && span.isEnd ? 4
                                  : span.isStart ? "4px 0 0 4px"
                                  : span.isEnd   ? "0 4px 4px 0" : 0,
                                cursor:"pointer",
                                display:"flex", alignItems:"center",
                                paddingLeft: span.isStart ? 6 : 2,
                                overflow:"hidden",
                                transition:"filter 0.15s, transform 0.1s",
                                zIndex:2,
                                boxShadow:"0 1px 4px rgba(0,0,0,0.2)",
                              }}
                              onMouseEnter={e => { e.currentTarget.style.filter="brightness(1.15)"; e.currentTarget.style.zIndex=3; }}
                              onMouseLeave={e => { e.currentTarget.style.filter="brightness(1)"; e.currentTarget.style.zIndex=2; }}
                            >
                              {!isTiny && (
                                <span style={{ fontSize:11, fontWeight:700, color:c.text,
                                  whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                                  {span.isStart ? b.guestName : ""}
                                </span>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Booking detail popup */}
      {selected && (
        <BookingDetail
          booking={selected.booking}
          color={STATUS_COLORS[selected.booking.status]}
          anchorRect={selected.rect}
          onClose={() => setSelected(null)}
          onInvoice={onInvoice}
        />
      )}
    </div>
  );
}
